import { useEffect } from 'preact/hooks';
import { RETURN_REASONS, lineAmount, stockRow } from '@khata/core';
import { DateInput, Field, MoneyInput, QtyInput, Select, TextInput } from '../components/index.js';
import { Picker } from '../components/Picker.js';
import { AccountPicker } from '../components/AccountPicker.js';
import { fromApi, isErr, money, quantity, useFields, useSave } from '../lib/form.js';
import { qty as fmtQty, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { master, state, submit, today } from '../lib/khata.js';
import { returnReason } from '../lib/labels.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function ReturnSheet(props: { itemId?: string }) {
  const m = master.value;
  const s = state.value;
  const startItem = props.itemId ? m.items.get(props.itemId) : undefined;
  const startRow = startItem ? stockRow(s, startItem) : null;
  const accounts = [...m.accounts.values()].filter((a) => a.active);
  const [f, set] = useFields({
    kind: startRow && startRow.defQty > 0 ? 'purchase' : 'sale',
    partyId: '',
    itemId: props.itemId ?? '',
    qty: '1',
    amount: '',
    reason: 'defective',
    bin: 'defective',
    accountId: accounts[0]?.id ?? '',
    date: today.value,
    note: '',
  });
  const [error, busy, save] = useSave();
  const isSale = f.kind === 'sale';
  const parties = [...m.parties.values()].filter(
    (p) => p.active && p.type === (isSale ? 'customer' : 'vendor'),
  );
  const items = [...m.items.values()]
    .filter((i) => i.active || i.id === f.itemId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const item = m.items.get(f.itemId);
  const row = item ? stockRow(s, item) : null;

  // Defective goods go to the bin by default; anything else back to stock.
  useEffect(() => {
    if (isSale) set('bin', f.reason === 'defective' ? 'defective' : 'stock');
    else set('bin', row && row.defQty > 0 ? 'defective' : 'stock');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.reason, f.kind, f.itemId]);

  const q = quantity(f.qty, 'qty');
  const autoAmount =
    item && !isErr(q)
      ? isSale
        ? lineAmount(q, item.sellPrice)
        : lineAmount(q, row?.avgCost ?? (0 as never))
      : null;

  const onSave = () =>
    save(async () => {
      if (!f.itemId) return { message: t('return_chooseItem'), field: 'itemId' };
      if (isErr(q)) return q;
      let amount: number | null = null;
      if (f.amount.trim() !== '') {
        const a = money(f.amount, 'amount');
        if (isErr(a)) return a;
        amount = a;
      }
      const r = await submit('return', {
        date: f.date,
        kind: f.kind,
        partyId: f.partyId || null,
        itemId: f.itemId,
        qty: q,
        amount,
        reason: f.reason,
        bin: f.bin,
        accountId: f.accountId,
        note: f.note,
      });
      if (!r.ok) return fromApi(r.error);
      return r.entry.type === 'return'
        ? t('return_recorded', { amount: taka(r.entry.amount) })
        : t('return_recorded', { amount: '' });
    });

  const help = isSale
    ? f.bin === 'defective'
      ? t('return_help_saleDefective')
      : t('return_help_saleStock')
    : f.bin === 'defective'
      ? t('return_help_purchaseDefective')
      : t('return_help_purchaseStock');

  return (
    <Sheet title={t('sheet_return')} footer={<SaveButton busy={busy} onClick={onSave} />}>
      <Field label={t('return_type')}>
        <Select value={f.kind} onChange={(v) => (set('kind', v), set('partyId', ''))}>
          <option value="sale">{t('return_customerReturned')}</option>
          <option value="purchase">{t('return_weReturn')}</option>
        </Select>
      </Field>
      <Field label={isSale ? t('customer') : t('vendor')}>
        <Picker
          value={f.partyId}
          onChange={(v) => set('partyId', v)}
          placeholder={isSale ? t('picker_customer') : t('picker_vendor')}
          noneLabel={isSale ? t('return_walkIn') : t('return_noVendor')}
          invalid={error?.field === 'partyId'}
          testId="party-picker"
          options={parties.map((p) => ({ id: p.id, label: p.name, sub: p.phone || p.note }))}
        />
      </Field>
      <Field label={t('return_item')}>
        <Picker
          value={f.itemId}
          onChange={(v) => set('itemId', v)}
          placeholder={t('picker_item')}
          invalid={error?.field === 'itemId'}
          testId="item-picker"
          options={items.map((i) => {
            const r = stockRow(s, i);
            return {
              id: i.id,
              label: i.name,
              sub: `${i.origin ? i.origin + ' · ' : ''}${fmtQty(r.qty)} ${t('return_inStock')}${r.defQty > 0 ? `, ${fmtQty(r.defQty)} ${t('return_defectiveCount')}` : ''}`,
            };
          })}
        />
      </Field>
      <div class="grid-2">
        <Field label={t('quantity')}>
          <QtyInput value={f.qty} onInput={(v) => set('qty', v)} invalid={error?.field === 'qty'} />
        </Field>
        <Field label={t('amountTaka')}>
          <MoneyInput
            value={f.amount}
            onInput={(v) => set('amount', v)}
            placeholder={
              autoAmount !== null
                ? t('return_auto', { amount: taka(autoAmount) })
                : t('return_autoFromPrice')
            }
            invalid={error?.field === 'amount'}
          />
        </Field>
      </div>
      <div class="grid-3">
        <Field label={t('return_reason')}>
          <Select value={f.reason} onChange={(v) => set('reason', v)}>
            {RETURN_REASONS.map((r) => (
              <option key={r} value={r}>
                {returnReason(r)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={isSale ? t('return_goesTo') : t('return_takeFrom')}>
          <Select value={f.bin} onChange={(v) => set('bin', v)}>
            <option value="stock">{t('stock')}</option>
            <option value="defective">{t('defectiveBin')}</option>
          </Select>
        </Field>
        <Field label={f.partyId ? t('return_refundViaIfCash') : t('return_refundVia')}>
          <AccountPicker
            value={f.accountId}
            onChange={(v) => set('accountId', v)}
            accounts={accounts}
            testId="account-picker"
          />
        </Field>
      </div>
      <div class="help">{help}</div>
      <div class="grid-2">
        <Field label={t('date')}>
          <DateInput
            value={f.date}
            onInput={(v) => set('date', v)}
            invalid={error?.field === 'date'}
          />
        </Field>
        <Field label={t('note')}>
          <TextInput value={f.note} onInput={(v) => set('note', v)} placeholder={t('optional')} />
        </Field>
      </div>
      <FormErrorLine error={error} />
    </Sheet>
  );
}
